import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE } from "../config";
import { authHeaders } from "../utils/authHeaders";
import Sidebar from "../components/Sidebar";
import "./table.css";

export default function AgentGeoMapping() {
  const { agentId } = useParams();
  const navigate = useNavigate();

  const [agent, setAgent] = useState(null);
  const [villages, setVillages] = useState([]);
  const [selectedVillageIds, setSelectedVillageIds] = useState(new Set());

  const [restrictionStatus, setRestrictionStatus] = useState("");
  const [assignedVillageCount, setAssignedVillageCount] = useState(0);
  const [warning, setWarning] = useState("");

  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadMapping() {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/agent-geo-mapping/${agentId}`, {
        headers: authHeaders(),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || `API failed: ${res.status}`);
      }

      setAgent(data.agent || null);
      setVillages(Array.isArray(data.villages) ? data.villages : []);
      setSelectedVillageIds(
        new Set((data.mapped_village_ids || []).map((x) => Number(x)))
      );

      setRestrictionStatus(data.restriction_status || "");
      setAssignedVillageCount(data.assigned_village_count || 0);
      setWarning(data.warning || "");
    } catch (e) {
      setError(e.message || "Failed to load agent geography mapping");
      setVillages([]);
      setSelectedVillageIds(new Set());
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const filteredVillages = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return villages;

    return villages.filter((v) => {
      const text = [
        v.state_name,
        v.state_code,
        v.district_name,
        v.district_code,
        v.block_name,
        v.block_code,
        v.panchayat_name,
        v.panchayat_code,
        v.village_name,
        v.village_code,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(q);
    });
  }, [villages, search]);

  const selectedCount = selectedVillageIds.size;

  function toggleVillage(villageId) {
    const id = Number(villageId);

    setSelectedVillageIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  function selectAllFiltered() {
    setSelectedVillageIds((prev) => {
      const next = new Set(prev);

      filteredVillages.forEach((v) => {
        next.add(Number(v.village_id));
      });

      return next;
    });
  }

  function clearFiltered() {
    setSelectedVillageIds((prev) => {
      const next = new Set(prev);

      filteredVillages.forEach((v) => {
        next.delete(Number(v.village_id));
      });

      return next;
    });
  }

  function clearAll() {
    if (
      !window.confirm(
        "Clear all village mapping for this agent? This will make the agent Not Restricted and able to access all FPO-mapped villages."
      )
    ) {
      return;
    }

    setSelectedVillageIds(new Set());
  }

  async function saveMapping() {
    setSaving(true);
    setError("");

    try {
      const villageIds = Array.from(selectedVillageIds);

      const res = await fetch(`${API_BASE}/agent-geo-mapping/${agentId}`, {
        method: "POST",
        headers: authHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          village_ids: villageIds,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || `API failed: ${res.status}`);
      }

      alert(data.message || "Agent village mapping saved");
      await loadMapping();
    } catch (e) {
      setError(e.message || "Failed to save agent village mapping");
    } finally {
      setSaving(false);
    }
  }

  const statusBox =
    restrictionStatus === "RESTRICTED" ? (
      <div
        style={{
          padding: 12,
          border: "1px solid #b7dfc0",
          background: "#f3fff5",
          borderRadius: 10,
          marginTop: 12,
        }}
      >
        <b>Status: Restricted</b>
        <div style={{ marginTop: 4 }}>
          This agent can add households only in assigned villages.
        </div>
        <div style={{ marginTop: 4 }}>
          Assigned villages: <b>{assignedVillageCount}</b>
        </div>
      </div>
    ) : (
      <div
        style={{
          padding: 12,
          border: "1px solid #f0d48a",
          background: "#fff9e8",
          borderRadius: 10,
          marginTop: 12,
        }}
      >
        <b>Status: Not Restricted</b>
        <div style={{ marginTop: 4 }}>
          {warning ||
            "No village mapping exists. This agent can access all FPO-mapped villages."}
        </div>
        <div style={{ marginTop: 6, color: "#7a5200" }}>
          To restrict this agent, select at least one village and save.
        </div>
      </div>
    );

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />

      <div style={{ padding: 20, width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Agent Geography Mapping</h2>
            <div style={{ color: "#666", marginTop: 4 }}>
              Assign villages to restrict agent household mapping geography.
            </div>
          </div>

          <button className="btn" onClick={() => navigate("/agents")}>
            Back to Agents
          </button>
        </div>

        {loading ? (
          <div style={{ marginTop: 20 }}>Loading...</div>
        ) : (
          <>
            {error && (
              <div
                style={{
                  marginTop: 12,
                  padding: 10,
                  background: "#fff5f5",
                  border: "1px solid #f0caca",
                  borderRadius: 8,
                  color: "#9b0000",
                }}
              >
                {error}
              </div>
            )}

            <div
              style={{
                marginTop: 14,
                padding: 14,
                border: "1px solid #eee",
                borderRadius: 12,
                background: "#fff",
              }}
            >
              <div>
                Agent:{" "}
                <b>
                  {agent?.name || "—"} {agent?.phone ? `(${agent.phone})` : ""}
                </b>
              </div>

              {statusBox}
            </div>

            <div
              style={{
                marginTop: 14,
                padding: 14,
                border: "1px solid #eee",
                borderRadius: 12,
                background: "#fff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <b>Village Selection</b>
                  <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
                    Selected now: <b>{selectedCount}</b> village(s). If selected
                    count is zero, the agent remains Not Restricted.
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn" onClick={selectAllFiltered}>
                    Select Filtered
                  </button>

                  <button className="btn" onClick={clearFiltered}>
                    Clear Filtered
                  </button>

                  <button className="btn" onClick={clearAll}>
                    Clear All
                  </button>

                  <button
                    className="btn"
                    onClick={saveMapping}
                    disabled={saving}
                    style={{
                      background: "#0066ff",
                      color: "white",
                    }}
                  >
                    {saving ? "Saving..." : "Save Mapping"}
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search state, district, block, panchayat, village or code"
                  style={{
                    width: "100%",
                    maxWidth: 520,
                    padding: 10,
                    border: "1px solid #ccc",
                    borderRadius: 6,
                  }}
                />
              </div>

              <div style={{ marginTop: 12, overflowX: "auto" }}>
                <table className="styled-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>State</th>
                      <th>District</th>
                      <th>Block</th>
                      <th>Panchayat</th>
                      <th>Village</th>
                      <th>Village Code</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredVillages.map((v) => {
                      const villageId = Number(v.village_id);
                      const checked = selectedVillageIds.has(villageId);

                      return (
                        <tr key={villageId}>
                          <td>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleVillage(villageId)}
                            />
                          </td>
                          <td>
                            {v.state_name || ""}
                            {v.state_code ? ` (${v.state_code})` : ""}
                          </td>
                          <td>
                            {v.district_name || ""}
                            {v.district_code ? ` (${v.district_code})` : ""}
                          </td>
                          <td>
                            {v.block_name || ""}
                            {v.block_code ? ` (${v.block_code})` : ""}
                          </td>
                          <td>
                            {v.panchayat_name || ""}
                            {v.panchayat_code ? ` (${v.panchayat_code})` : ""}
                          </td>
                          <td>{v.village_name || ""}</td>
                          <td>{v.village_code || ""}</td>
                        </tr>
                      );
                    })}

                    {filteredVillages.length === 0 && (
                      <tr>
                        <td colSpan="7" style={{ padding: 12, color: "#777" }}>
                          No villages found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}